import type { NotificationPolicyDefinitionDto } from '@solidops/shared/notifications';
import {
  notificationPolicyCreateSchema,
  notificationPolicyUpdateSchema,
} from '@solidops/shared/notifications';
import { ApiError } from '../api/client.js';
export type NotificationPolicyCommand = {
  readonly teamId: string;
  readonly policyId: string | null;
  readonly body: NotificationPolicyDefinitionDto & { readonly expectedVersion?: number };
  readonly key: string;
  readonly fingerprint: string;
};
export function freezeNotificationPolicyCommand(
  teamId: string,
  policyId: string | null,
  definition: NotificationPolicyDefinitionDto,
  version: number | undefined,
  previous: NotificationPolicyCommand | null,
): NotificationPolicyCommand {
  const body = policyId
    ? notificationPolicyUpdateSchema.parse({ ...definition, expectedVersion: version })
    : notificationPolicyCreateSchema.parse(definition);
  const fingerprint = JSON.stringify({ teamId, policyId, body });
  return {
    teamId,
    policyId,
    body: structuredClone(body),
    fingerprint,
    key: previous?.fingerprint === fingerprint ? previous.key : crypto.randomUUID(),
  };
}
export function notificationPolicyFailure(error: unknown): 'known' | 'stale' | 'unknown' {
  if (
    error instanceof ApiError &&
    error.status === 409 &&
    (error.code === 'VERSION_CONFLICT' || error.code === 'IDEMPOTENCY_CONFLICT')
  )
    return 'stale';
  return !(error instanceof ApiError) || error.status >= 500 ? 'unknown' : 'known';
}

export function prepareNotificationPolicyCommand(
  teamId: string,
  policyId: string | null,
  definition: NotificationPolicyDefinitionDto,
  version: number | undefined,
  previous: NotificationPolicyCommand | null,
): NotificationPolicyCommand | null {
  const result = policyId
    ? notificationPolicyUpdateSchema.safeParse({ ...definition, expectedVersion: version })
    : notificationPolicyCreateSchema.safeParse(definition);
  if (!result.success) return null;
  return freezeNotificationPolicyCommand(teamId, policyId, result.data, version, previous);
}

/** Scope cached-read recovery to the editor that owns its paused mutation/draft state. */
export function isNotificationPolicyEditorPath(pathname: string): boolean {
  const uuid =
    '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}';
  return new RegExp(
    `^(?:/settings/notification-policies|/teams/${uuid}/escalations)/(?:new|${uuid})/?$`,
  ).test(pathname);
}
